import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  X,
  User,
  Package,
  Loader2,
  CheckCircle2,
  AlertCircle,
  MapPin,
  LocateFixed,
  Router,
  Radio,
  Wifi,
  Eye,
  EyeOff,
  Handshake,
  Calendar,
  Shuffle,
  ArrowRight,
  ArrowLeft,
  Plus,
} from "lucide-react";
import { PhotoPicker, AuthImage, uploadFoto } from "./PhotoField";

// Fix leaflet marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_CENTER = [-6.9147, 107.6098];

const EMPTY = {
  no_layanan: "",
  nama: "",
  nik: "",
  no_hp: "",
  email: "",
  alamat: "",
  rt: "",
  rw: "",
  wilayah_id: "",
  desa: "",
  kecamatan: "",
  kabupaten: "",
  area: "",
  lat: DEFAULT_CENTER[0],
  lng: DEFAULT_CENTER[1],
  paket_id: "",
  mikrotik_router_id: "",
  mikrotik_profile: "",
  username_pppoe: "",
  password_pppoe: "",
  odp_id: "",
  odp_port: "",
  mitra_id: "",
  mitra_sharing: [],
  tanggal_pasang: new Date().toISOString().slice(0, 10),
  tanggal_tagihan: new Date().toISOString().slice(0, 10),
  jatuh_tempo_hari: 10,
  status_layanan: "aktif",
  tipe_koneksi: "PPPoE",
  auto_provision_mikrotik: true,
};

const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => { if (position) map.setView(position, 17); }, [position, map]);
  return null;
};

const MapClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
  return null;
};

const randomPassword = () => Math.random().toString(36).slice(-8);

export const AddPelangganModal = ({ initial, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isOwnerAdmin = user?.role === "admin"; // hanya owner/super admin melihat sharing mitra
  const isEdit = !!initial?.id;
  const [tab, setTab] = useState("identitas");
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY;
    const ms = (initial.mitra_sharing && initial.mitra_sharing.length)
      ? initial.mitra_sharing
      : (initial.mitra_id ? [{ mitra_id: initial.mitra_id, persen: "" }] : []);
    return { ...EMPTY, ...initial, mitra_sharing: ms };
  });
  const [wilayahList, setWilayahList] = useState([]);
  const [paketList, setPaketList] = useState([]);
  const [routerList, setRouterList] = useState([]);
  const [mitraList, setMitraList] = useState([]);
  const [odpList, setOdpList] = useState([]);
  const [fotoFile, setFotoFile] = useState(null);
  const [removeFoto, setRemoveFoto] = useState(false);
  const [fotoError, setFotoError] = useState("");
  const [portInfo, setPortInfo] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [locating, setLocating] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Initial data
  useEffect(() => {
    (async () => {
      try {
        const [wil, paket, routers, mitra] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/master/paket`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/jaringan/mikrotik/routers`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/master/mitra`, { withCredentials: true }),
        ]);
        setWilayahList(wil.data);
        setPaketList(paket.data);
        setRouterList(routers.data);
        setMitraList(mitra.data);
      } catch (err) { console.error(err); }
    })();
  }, []);

  // Load ODP when wilayah changes
  useEffect(() => {
    if (!form.wilayah_id) { setOdpList([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/master/odp/by-wilayah/${form.wilayah_id}`, { withCredentials: true });
        setOdpList(res.data);
      } catch (err) { console.error(err); }
    })();
  }, [form.wilayah_id]);

  // Load available ports when ODP changes
  useEffect(() => {
    if (!form.odp_id) { setPortInfo(null); return; }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/master/odp/${form.odp_id}/ports`, { withCredentials: true });
        setPortInfo(res.data);
      } catch (err) { console.error(err); }
    })();
  }, [form.odp_id]);

  // Load PPPoE profiles when router changes
  useEffect(() => {
    if (!form.mikrotik_router_id) { setProfiles([]); return; }
    (async () => {
      setLoadingProfiles(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/jaringan/mikrotik/routers/${form.mikrotik_router_id}/pppoe/profiles`, { withCredentials: true });
        setProfiles(res.data.profiles || []);
      } catch (err) { console.error(err); setProfiles([]); }
      finally { setLoadingProfiles(false); }
    })();
  }, [form.mikrotik_router_id]);

  // Handlers
  const handleWilayahChange = (id) => {
    const w = wilayahList.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      wilayah_id: id,
      desa: w?.desa || "",
      kecamatan: w?.kecamatan || "",
      kabupaten: w?.kabupaten || "",
      area: w?.nama_wilayah || "",
      odp_id: "",  // reset ODP karena wilayah berubah
      odp_port: "",
      // buang baris mitra yang wilayahnya tidak mencakup wilayah baru
      mitra_sharing: (f.mitra_sharing || []).filter((row) => {
        if (!row.mitra_id) return true;
        const m = mitraList.find((x) => x.id === row.mitra_id);
        const wids = m?.wilayah_ids || [];
        return wids.length === 0 || wids.includes(id);
      }),
    }));
  };

  const handlePaketChange = (id) => {
    const p = paketList.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      paket_id: id,
      mikrotik_profile: p?.mikrotik_profile || f.mikrotik_profile,
    }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) { setError("Browser tidak support geolocation"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setLocating(false);
      },
      (err) => { setError(`Gagal deteksi lokasi: ${err.message}`); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const generateUsername = () => {
    if (!form.nama) return;
    const base = form.nama.toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 15);
    setField("username_pppoe", base);
  };

  const handleGeneratePassword = () => setField("password_pppoe", randomPassword());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const cleanedShare = (form.mitra_sharing || [])
        .filter((en) => en.mitra_id && (parseFloat(en.persen) || 0) > 0)
        .map((en) => ({ mitra_id: en.mitra_id, persen: parseFloat(en.persen) }));
      const sumShare = cleanedShare.reduce((s, en) => s + en.persen, 0);
      if (sumShare > 100) { setError("Total porsi sharing mitra tidak boleh melebihi 100%"); setLoading(false); return; }
      const payload = {
        ...form,
        mitra_sharing: cleanedShare,
        mitra_id: "",
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        jatuh_tempo_hari: parseInt(form.jatuh_tempo_hari) || 10,
        odp_port: form.odp_port ? parseInt(form.odp_port) : 0,
      };
      const url = isEdit ? `${BACKEND_URL}/api/pelanggan/${initial.id}` : `${BACKEND_URL}/api/pelanggan`;
      const method = isEdit ? "put" : "post";
      const res = await axios[method](url, payload, { withCredentials: true });
      setResult(res.data);
      const pelId = isEdit ? initial.id : res.data.id;
      let fotoFailed = false;
      setFotoError("");
      try {
        if (fotoFile && pelId) await uploadFoto(`/api/pelanggan/${pelId}/foto-rumah`, fotoFile);
        else if (removeFoto && initial?.foto_rumah_path) await axios.delete(`${BACKEND_URL}/api/pelanggan/${pelId}/foto-rumah`, { withCredentials: true });
      } catch (err) {
        const msg = `Data pelanggan tersimpan, tapi foto rumah gagal diunggah: ${err.response?.data?.detail || "coba lagi"}`;
        if (!isEdit && !res.data.provision_error) {
          alert(`${msg}. Unggah ulang lewat tombol Edit.`);
          onSuccess?.(res.data); onClose();
          return;
        }
        fotoFailed = true;
        setFotoError(msg);
      }
      if (res.data.provision_error || fotoFailed) {
        // Pelanggan SUDAH tersimpan, tapi provisioning router gagal → refresh list,
        // tapi biarkan modal terbuka agar admin membaca alasannya.
        onSuccess?.(res.data, { keepOpen: true });
      } else {
        setTimeout(() => { onSuccess?.(res.data); onClose(); }, 2000);
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status) {
        setError(`Gagal menyimpan pelanggan (server error ${err.response.status}). Coba lagi sebentar.`);
      } else {
        setError("Gagal menyimpan pelanggan — permintaan timeout / tidak sampai ke server. Ini biasanya karena router MikroTik sedang tidak online sehingga proses auto-provisioning menggantung. Solusi: nonaktifkan 'Auto-generate ke Router MikroTik' lalu simpan, atau pastikan router online, kemudian coba lagi.");
      }
    } finally { setLoading(false); }
  };

  const position = [parseFloat(form.lat) || DEFAULT_CENTER[0], parseFloat(form.lng) || DEFAULT_CENTER[1]];
  const selectedProfile = profiles.find((p) => p.name === form.mikrotik_profile);
  const selectedOdp = odpList.find((o) => o.id === form.odp_id);
  const selectedRouter = routerList.find((r) => r.id === form.mikrotik_router_id);

  const identitasComplete = form.nama && form.nik && form.no_hp && form.alamat && form.wilayah_id;

  const shareRows = form.mitra_sharing || [];
  const totalSharePersen = shareRows.reduce((s, en) => s + (parseFloat(en.persen) || 0), 0);
  const pusatPersen = Math.max(0, 100 - totalSharePersen);
  // Hanya investor aktif yang wilayahnya mencakup wilayah pelanggan (wilayah_ids kosong = semua wilayah)
  const activeMitra = mitraList.filter((m) => {
    if (m.status !== "aktif") return false;
    const wids = m.wilayah_ids || [];
    if (wids.length === 0) return true;
    return form.wilayah_id ? wids.includes(form.wilayah_id) : false;
  });
  const updateShare = (i, k, v) => setForm((f) => {
    const arr = [...(f.mitra_sharing || [])];
    arr[i] = { ...arr[i], [k]: v };
    // Persen otomatis mengikuti prosentase_sharing di master data mitra
    if (k === "mitra_id") {
      const m = mitraList.find((x) => x.id === v);
      arr[i].persen = m ? (m.prosentase_sharing ?? "") : "";
    }
    return { ...f, mitra_sharing: arr };
  });
  const addShare = () => setForm((f) => ({ ...f, mitra_sharing: [...(f.mitra_sharing || []), { mitra_id: "", persen: "" }] }));
  const removeShare = (i) => setForm((f) => { const arr = [...(f.mitra_sharing || [])]; arr.splice(i, 1); return { ...f, mitra_sharing: arr }; });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="add-pelanggan-modal">
      <div className="w-full max-w-4xl rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-[#0a1120]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">{isEdit ? "Edit Pelanggan" : "Tambah Pelanggan Baru"}</h2>
              <p className="text-[11px] text-slate-400">{isEdit ? "Update data pelanggan" : "Isi identitas & detail paket/ODP"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1.5 rounded" data-testid="close-add-pelanggan"><X className="w-4 h-4" /></button>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-slate-800 bg-slate-950/60" data-testid="pelanggan-tabs">
          <button
            type="button"
            onClick={() => setTab("identitas")}
            data-testid="tab-identitas"
            className={`px-5 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
              tab === "identitas" ? "border-emerald-400 text-emerald-300" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${tab === "identitas" ? "bg-emerald-500 text-white" : identitasComplete ? "bg-emerald-600/40 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{identitasComplete ? "✓" : "1"}</span>
            <span>Identitas Pelanggan</span>
          </button>
          <button
            type="button"
            onClick={() => setTab("detail")}
            data-testid="tab-detail"
            className={`px-5 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
              tab === "detail" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${tab === "detail" ? "bg-cyan-500 text-white" : "bg-slate-700 text-slate-400"}`}>2</span>
            <span>Detail Paket & ODP</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Alerts */}
          {(error || result) && (
            <div className="p-3 border-b border-slate-800">
              {error && (
                <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-start gap-2" data-testid="add-pelanggan-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
                </div>
              )}
              {result && (
                <div className={`p-2.5 rounded-lg border text-xs ${result.provision_error ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"}`} data-testid="add-pelanggan-success">
                  <div className="flex items-start gap-2">
                    {result.provision_error ? <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    <div>
                      <p className="font-bold">{result.message}</p>
                      <p className="opacity-80 mt-1">No. Layanan: <span className="font-mono">{result.no_layanan}</span></p>
                      {result.provision_result && <p className="text-emerald-400/80 mt-1">✓ Auto-provisioned PPPoE secret ke MikroTik</p>}
                      {result.provision_error && <p className="text-amber-300 mt-1">⚠ Alasan provisioning gagal: {result.provision_error}</p>}
                      {result.provision_error && <button type="button" onClick={onClose} className="mt-2 px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg text-amber-100 font-semibold" data-testid="add-pelanggan-close-after-warn">Tutup</button>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB IDENTITAS */}
          {tab === "identitas" && (
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tab-identitas-content">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">ID Pelanggan</label>
                    <input type="text" value={form.no_layanan} onChange={(e) => setField("no_layanan", e.target.value)} placeholder="Auto-generate" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-id-input" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Lengkap *</label>
                    <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-nama-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">NIK *</label>
                    <input required type="text" value={form.nik} onChange={(e) => setField("nik", e.target.value)} placeholder="16 digit KTP" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-nik-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">No HP / WA *</label>
                    <input required type="text" value={form.no_hp} onChange={(e) => setField("no_hp", e.target.value)} placeholder="081234567890" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-hp-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">Alamat *</label>
                  <textarea required rows={2} value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} placeholder="Jl. Sukajadi No. 12" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-alamat-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">RT</label>
                    <input type="text" value={form.rt} onChange={(e) => setField("rt", e.target.value)} placeholder="04" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-rt-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">RW</label>
                    <input type="text" value={form.rw} onChange={(e) => setField("rw", e.target.value)} placeholder="02" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-rw-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-purple-400" />Wilayah *</label>
                  <select required value={form.wilayah_id} onChange={(e) => handleWilayahChange(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-wilayah-select">
                    <option value="">-- Pilih Wilayah --</option>
                    {wilayahList.map((w) => <option key={w.id} value={w.id}>{w.kode_wilayah} · {w.nama_wilayah}</option>)}
                  </select>
                  {form.wilayah_id && (
                    <p className="text-[10px] text-purple-400 mt-1 font-mono">Auto: {form.desa}, {form.kecamatan}, {form.kabupaten}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Latitude</label>
                    <input type="number" step="any" value={form.lat} onChange={(e) => setField("lat", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Longitude</label>
                    <input type="number" step="any" value={form.lng} onChange={(e) => setField("lng", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" />
                  </div>
                </div>

                <button type="button" onClick={handleUseCurrentLocation} disabled={locating} className="w-full py-2 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-bold flex items-center justify-center gap-2 hover:from-cyan-500/30 hover:to-blue-500/30 disabled:opacity-50" data-testid="pel-current-location-btn">
                  {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />}
                  {locating ? "Mendeteksi GPS..." : "Gunakan Lokasi Rumah Sekarang"}
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1"><MapPin className="w-3 h-3 text-cyan-400" />Klik peta untuk set titik rumah pelanggan</label>
                <div className="rounded-xl overflow-hidden border border-slate-700" style={{ height: "380px" }} data-testid="pel-map-container">
                  <MapContainer center={position} zoom={17} style={{ height: "100%", width: "100%" }}>
                    <LayersControl position="topright">
                      <LayersControl.BaseLayer checked name="Satellite">
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Tiles &copy; Esri" maxZoom={19} />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Street">
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png" attribution="&copy; OpenStreetMap" />
                      </LayersControl.BaseLayer>
                    </LayersControl>
                    <Marker position={position} />
                    <RecenterMap position={position} />
                    <MapClickHandler onPick={(latlng) => setForm((f) => ({ ...f, lat: latlng[0], lng: latlng[1] }))} />
                  </MapContainer>
                </div>
                <p className="text-[10px] text-slate-500">Tips: Zoom in dan klik atap rumah untuk titik presisi</p>

                <button type="button" onClick={() => setTab("detail")} disabled={!identitasComplete} className="w-full mt-2 py-2 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="next-to-detail-btn">
                  Lanjut ke Detail Paket & ODP <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* TAB DETAIL PAKET & ODP */}
          {tab === "detail" && (
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tab-detail-content">
              {/* Left column: Paket & Mikrotik */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Package className="w-3 h-3 text-cyan-400" />Paket Internet *</label>
                  <select required value={form.paket_id} onChange={(e) => handlePaketChange(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-paket-select">
                    <option value="">-- Pilih Paket --</option>
                    {paketList.map((p) => <option key={p.id} value={p.id}>{p.nama} — {p.download_speed}M/{p.upload_speed}M — Rp {Number(p.harga).toLocaleString("id-ID")}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Router className="w-3 h-3 text-blue-400" />Router MikroTik *</label>
                  <select required value={form.mikrotik_router_id} onChange={(e) => setField("mikrotik_router_id", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-router-select">
                    <option value="">-- Pilih Router --</option>
                    {routerList.map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.host}:{r.port}) {r.last_status === "online" ? "🟢" : "🔴"}</option>)}
                  </select>
                  {routerList.length === 0 && <p className="text-[10px] text-amber-400 mt-1">Belum ada router terdaftar. Tambahkan di menu Jaringan → Mikrotik dulu.</p>}
                </div>

                {form.mikrotik_router_id && (
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Wifi className="w-3 h-3 text-emerald-400" />Profile PPPoE (dari Mikrotik)</label>
                    {loadingProfiles ? (
                      <div className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin text-cyan-400" />Memuat profile...</div>
                    ) : (
                      <select value={form.mikrotik_profile} onChange={(e) => setField("mikrotik_profile", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="pel-profile-select">
                        <option value="">-- Pilih Profile --</option>
                        {profiles.map((p) => <option key={p.name} value={p.name}>{p.name}{p.rate_limit ? ` — ${p.rate_limit}` : ""}</option>)}
                      </select>
                    )}
                    {selectedProfile && (
                      <p className="text-[10px] text-emerald-400 mt-1 font-mono">↓{selectedProfile.download || "?"} · ↑{selectedProfile.upload || "?"}</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Username PPPoE *</label>
                    <div className="relative">
                      <input required type="text" value={form.username_pppoe} onChange={(e) => setField("username_pppoe", e.target.value.toLowerCase().replace(/\s/g, "_"))} placeholder="ahmad_fauzi" className="w-full px-3 py-2 pr-8 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-username-input" />
                      <button type="button" onClick={generateUsername} title="Generate dari nama" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400"><Shuffle className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Password PPPoE *</label>
                    <div className="relative">
                      <input required type={showPass ? "text" : "password"} value={form.password_pppoe} onChange={(e) => setField("password_pppoe", e.target.value)} className="w-full px-3 py-2 pr-16 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-password-input" />
                      <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400">{showPass ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                      <button type="button" onClick={handleGeneratePassword} title="Generate random" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400"><Shuffle className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>

                <label className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 cursor-pointer">
                  <input type="checkbox" checked={form.auto_provision_mikrotik} onChange={(e) => setField("auto_provision_mikrotik", e.target.checked)} className="w-4 h-4 accent-emerald-500" data-testid="pel-auto-provision-toggle" />
                  <div>
                    <p className="text-xs font-bold text-emerald-200">Auto-generate ke Router MikroTik</p>
                    <p className="text-[10px] text-emerald-400/80">Otomatis create secret PPPoE (username & password) di router terpilih jika belum ada</p>
                  </div>
                </label>
              </div>

              {/* Right column: ODP + Mitra + Dates */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Radio className="w-3 h-3 text-cyan-400" />ODP (di wilayah pelanggan) *</label>
                  <select required value={form.odp_id} onChange={(e) => setField("odp_id", e.target.value)} disabled={!form.wilayah_id} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 disabled:opacity-50" data-testid="pel-odp-select">
                    <option value="">{form.wilayah_id ? "-- Pilih ODP --" : "-- Pilih wilayah dulu --"}</option>
                    {odpList.map((o) => <option key={o.id} value={o.id} disabled={o.tersedia === 0}>{o.kode} · {o.nama} ({o.tersedia}/{o.kapasitas} port tersedia){o.tersedia === 0 ? " — PENUH" : ""}</option>)}
                  </select>
                  {form.wilayah_id && odpList.length === 0 && <p className="text-[10px] text-amber-400 mt-1">Belum ada ODP di wilayah ini. Tambahkan di Master Data → Data ODP.</p>}
                  {selectedOdp && (
                    <div className="mt-2" data-testid="pel-odp-foto">
                      <p className="text-[10px] text-slate-400 mb-1">Foto ODP {selectedOdp.kode} (otomatis dari Data ODP)</p>
                      <AuthImage path={selectedOdp.foto_path} alt={`Foto ${selectedOdp.kode}`} className="w-full h-32 rounded-xl" emptyText="ODP ini belum punya foto" testid="pel-odp-foto-img" />
                    </div>
                  )}
                </div>

                <PhotoPicker
                  label="Foto Rumah Pelanggan"
                  existingPath={removeFoto ? null : initial?.foto_rumah_path}
                  file={fotoFile}
                  onFileChange={(f) => { setFotoFile(f); if (f) setRemoveFoto(false); }}
                  onRemoveExisting={() => setRemoveFoto(true)}
                  testid="pel-foto-rumah"
                />
                {fotoError && <p className="text-[11px] text-rose-400" data-testid="pel-foto-error">{fotoError}</p>}

                {form.odp_id && portInfo && (
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1">Port ODP * <span className="text-slate-500 font-normal">(pilih port yang tersedia)</span></label>
                    <div className="grid grid-cols-8 gap-1.5" data-testid="pel-port-grid">
                      {portInfo.ports.map((p) => {
                        const isSelected = parseInt(form.odp_port) === p.port;
                        return (
                          <button
                            key={p.port}
                            type="button"
                            onClick={() => p.available && setField("odp_port", p.port)}
                            disabled={!p.available}
                            title={p.used_by ? `Terpakai: ${p.used_by}` : `Port ${p.port} tersedia`}
                            className={`aspect-square rounded-lg border text-[11px] font-mono font-bold flex items-center justify-center transition-colors ${
                              isSelected ? "bg-emerald-500 text-white border-emerald-400 shadow-lg shadow-emerald-500/40" :
                              p.available ? "bg-slate-800 text-emerald-300 border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10" :
                              "bg-slate-900 text-slate-600 border-slate-800 cursor-not-allowed line-through"
                            }`}
                            data-testid={`pel-port-${p.port}`}
                          >
                            {p.port}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">{portInfo.tersedia} tersedia dari {portInfo.kapasitas} port di {portInfo.odp_kode}</p>
                  </div>
                )}

                {isOwnerAdmin && (
                <div data-testid="pel-mitra-sharing">
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Handshake className="w-3 h-3 text-amber-400" />Mitra / Investor Sharing (bisa lebih dari 1)</label>
                  {!form.wilayah_id && <p className="text-[10px] text-amber-400 mb-2">Pilih wilayah pelanggan dulu — daftar investor difilter sesuai wilayah.</p>}
                  <div className="space-y-2">
                    {shareRows.length === 0 && <p className="text-[10px] text-slate-500">Belum ada mitra — 100% milik Pusat. Klik "Tambah Mitra" untuk membagi hasil pelanggan ini.</p>}
                    {shareRows.map((row, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`mitra-share-row-${i}`}>
                        <select value={row.mitra_id} onChange={(e) => updateShare(i, "mitra_id", e.target.value)} className="flex-1 px-2 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid={`mitra-share-select-${i}`}>
                          <option value="">-- Pilih Investor --</option>
                          {activeMitra.map((m) => <option key={m.id} value={m.id} disabled={shareRows.some((r, ri) => ri !== i && r.mitra_id === m.id)}>{m.nama} ({m.prosentase_sharing}%)</option>)}
                        </select>
                        <div className="relative w-24">
                          <input type="number" min={0} max={100} step="0.1" value={row.persen} onChange={(e) => updateShare(i, "persen", e.target.value)} placeholder="0" title="Otomatis dari prosentase sharing di Master Mitra (bisa diubah)" className="w-full px-2 py-2 pr-6 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid={`mitra-share-persen-${i}`} />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">%</span>
                        </div>
                        <button type="button" onClick={() => removeShare(i)} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg" data-testid={`mitra-share-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  {form.wilayah_id && activeMitra.length === 0 && <p className="text-[10px] text-slate-500 mt-1">Tidak ada investor aktif untuk wilayah ini. Atur wilayah investor di Master Data → Mitra.</p>}
                  <button type="button" onClick={addShare} disabled={!form.wilayah_id || activeMitra.length === 0} className="mt-2 px-3 py-1.5 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[11px] font-bold flex items-center gap-1 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-mitra-share-btn"><Plus className="w-3 h-3" /> Tambah Mitra</button>
                  <div className={`mt-2 p-2 rounded-lg text-[11px] flex items-center justify-between ${totalSharePersen > 100 ? "bg-rose-500/10 border border-rose-500/40 text-rose-300" : "bg-slate-950/60 border border-slate-800 text-slate-300"}`} data-testid="mitra-share-summary">
                    <span>Total Mitra: <span className="font-mono font-bold">{totalSharePersen.toFixed(1)}%</span></span>
                    <span>🏢 Pusat: <span className="font-mono font-bold text-emerald-300">{pusatPersen.toFixed(1)}%</span></span>
                  </div>
                  {totalSharePersen > 100 && <p className="text-[10px] text-rose-400 mt-1">Total porsi mitra melebihi 100%. Kurangi agar valid.</p>}
                </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Calendar className="w-3 h-3 text-cyan-400" />Tgl Registrasi</label>
                    <input type="date" value={form.tanggal_pasang} onChange={(e) => setField("tanggal_pasang", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-tanggal-pasang-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><Calendar className="w-3 h-3 text-amber-400" />Tgl Tagihan (Mulai)</label>
                    <input type="date" value={form.tanggal_tagihan} onChange={(e) => setField("tanggal_tagihan", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="pel-tanggal-tagihan-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">Jatuh Tempo (Tanggal ke- tiap bulan)</label>
                  <input type="number" min={1} max={28} value={form.jatuh_tempo_hari} onChange={(e) => setField("jatuh_tempo_hari", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" />
                </div>

                {/* Summary card */}
                {(selectedRouter || selectedOdp) && (
                  <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-1 text-[11px]">
                    <p className="text-slate-400 font-semibold">Ringkasan Setup:</p>
                    {selectedRouter && <p className="text-slate-300">🔧 Router: <span className="text-blue-300 font-mono">{selectedRouter.nama}</span></p>}
                    {form.mikrotik_profile && <p className="text-slate-300">📶 Profile: <span className="text-emerald-300 font-mono">{form.mikrotik_profile}</span></p>}
                    {selectedOdp && <p className="text-slate-300">📡 ODP: <span className="text-cyan-300 font-mono">{selectedOdp.kode}</span> port <span className="text-emerald-300 font-mono">{form.odp_port || "?"}</span></p>}
                    {form.username_pppoe && <p className="text-slate-300">👤 PPPoE: <span className="text-white font-mono">{form.username_pppoe}</span></p>}
                    {shareRows.length > 0 && <p className="text-slate-300">🤝 Mitra Sharing: <span className="text-amber-300">{shareRows.filter((r) => r.mitra_id).length} investor · {totalSharePersen.toFixed(0)}% (Pusat {pusatPersen.toFixed(0)}%)</span></p>}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="lg:col-span-2 flex items-center gap-2 pt-3 border-t border-slate-800">
                <button type="button" onClick={() => setTab("identitas")} className="py-2 px-4 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 flex items-center gap-1.5" data-testid="back-to-identitas-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Kembali
                </button>
                <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
                <button type="submit" disabled={loading || !!result} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-pelanggan-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Menyimpan & Provision...</> : result ? <><CheckCircle2 className="w-4 h-4" />Berhasil</> : <><CheckCircle2 className="w-4 h-4" />{isEdit ? "Update Pelanggan" : "Daftarkan & Generate PPPoE"}</>}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
